/**
 * useCurrentWell — resolves the currently-selected well (from wellContext, or
 * the first well as a sensible default) plus its events from Oxbo-P and the
 * "active" event (the last-opened job). The report screens hang off this so
 * the well switcher drives every screen consistently.
 */
import { useMemo } from "react";
import { useAppSelector } from "@app/hooks";
import { useGetWellsQuery } from "@api/mApi";
import { useGetWellEventsQuery } from "@api/pApi";
import type { WellView, EventOut } from "@api/types";

export interface CurrentWell {
  well: WellView | undefined;
  wells: WellView[];
  events: EventOut[];
  activeEvent: EventOut | undefined;
  isLoading: boolean;
  isError: boolean;
}

/** Pick the "active" event: prefer an open drilling job, else the first. */
function pickActiveEvent(events: EventOut[]): EventOut | undefined {
  if (events.length === 0) return undefined;
  const openDrilling = events.find((e) => e.event_type === "drilling" && !e.status_end);
  return openDrilling ?? events[0];
}

export function useCurrentWell(): CurrentWell {
  const selectedWellId = useAppSelector((s) => s.wellContext.selectedWellId);
  const activeEventId = useAppSelector((s) => s.wellContext.activeEventId);
  const { data: wells = [], isLoading: wellsLoading, isError: wellsError } = useGetWellsQuery();

  const well = useMemo(
    () => wells.find((w) => w.id === selectedWellId) ?? wells[0],
    [wells, selectedWellId],
  );

  const {
    data: events = [],
    isLoading: evLoading,
    isError: evError,
  } = useGetWellEventsQuery(well?.uwi ?? "", { skip: !well });

  // Honour an explicitly-chosen event; otherwise fall back to a sensible default.
  const activeEvent = useMemo(() => {
    if (activeEventId) {
      const chosen = events.find((e) => e.id === activeEventId);
      if (chosen) return chosen;
    }
    return pickActiveEvent(events);
  }, [events, activeEventId]);

  return {
    well,
    wells,
    events,
    activeEvent,
    isLoading: wellsLoading || evLoading,
    isError: wellsError || evError,
  };
}
